/**
 * Use setInterval in functional React component with the same API.
 * Set your callback function as a first parameter and a delay
 * (in milliseconds) for the second argument.
 * You can also stop the timer passing null instead the delay or even,
 * execute it right away passing 0.
 *
 * The main difference between the setInterval you know and this
 * useInterval hook is that its arguments are "dynamic".
 * You can get more information in the following Dan Abramov's
 * blog post:
 * https://overreacted.io/making-setinterval-declarative-with-react-hooks/
 *
 */
import { useEffect, useRef } from "react";

function useInterval(callback, delay) {
  let startImmediate = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
  const savedCallback = useRef(callback);
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);
  useEffect(() => {
    /**
     * Don't schedule if no delay is specified.
     * Note: 0 is a valid value for delay.
     */
    if (!delay && delay !== 0) {
      return;
    }

    if (startImmediate) {
      savedCallback.current();
    }

    const id = setInterval(() => savedCallback.current(), delay);
    return () => clearInterval(id);
  }, [delay, startImmediate]);
}

export default useInterval;